export type CoProbationUserInput =
  | "participantId"
  | "assignedColor"
  | "languageSelection"
  | "name"
  | "dateOfBirth";

export type CoProbationResultMode = "daily-test-status" | "active-color-status" | "unknown";

export type CoProbationPhoneLine = {
  id: string;
  phoneNumber: string;
  displayPhoneNumber: string;
  serviceProvider: string;
  documentedUserInputs: CoProbationUserInput[];
  possibleUserInputs: CoProbationUserInput[];
  resultMode: CoProbationResultMode;
  knownPromptExample: string | null;
  notes: string[];
  sourceDocumentIds: string[];
};

export type CoProbationProgramCallOption = {
  id: string;
  label: string;
  phoneLineId: string;
  possibleUserInputs: CoProbationUserInput[];
  notes: string[];
  sourceDocumentIds: string[];
};

export type CoProbationProgramCallProfile = {
  id: string;
  label: string;
  phoneLineId: string;
  phoneNumber: string;
  serviceProvider: string;
  resultMode: CoProbationResultMode;
  documentedUserInputs: CoProbationUserInput[];
  possibleUserInputs: CoProbationUserInput[];
  notes: string[];
};

export type CoProbationResearchGap = {
  id: string;
  summary: string;
};

const SENTRY_PROMPT_EXAMPLE =
  "You have reached the drug testing notification system, press 1 for english, enter your id number.";

function normalizePhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  const nationalDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (nationalDigits.length !== 10) {
    throw new Error(`Expected a 10-digit US phone number, received "${value}".`);
  }
  return `+1${nationalDigits}`;
}

function formatDisplayPhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, "").slice(-10);
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function uniqueInputs(values: readonly CoProbationUserInput[]): CoProbationUserInput[] {
  return [...new Set(values)];
}

function buildPhoneLine(
  id: string,
  rawPhoneNumber: string,
  serviceProvider: string,
  documentedUserInputs: readonly CoProbationUserInput[],
  possibleUserInputs: readonly CoProbationUserInput[],
  resultMode: CoProbationResultMode,
  notes: readonly string[] = [],
  sourceDocumentIds: readonly string[] = ["ua-testing-co-phones"],
  knownPromptExample: string | null = null
): CoProbationPhoneLine {
  const phoneNumber = normalizePhoneNumber(rawPhoneNumber);
  return {
    id,
    phoneNumber,
    displayPhoneNumber: formatDisplayPhoneNumber(phoneNumber),
    serviceProvider,
    documentedUserInputs: [...documentedUserInputs],
    possibleUserInputs: [...possibleUserInputs],
    resultMode,
    knownPromptExample,
    notes: [...notes],
    sourceDocumentIds: [...sourceDocumentIds],
  };
}

export const coProbationPhoneLines: CoProbationPhoneLine[] = [
  buildPhoneLine(
    "line-303-515-7638",
    "303-515-7638",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-303-785-6174",
    "303-785-6174",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-303-268-3010",
    "303-268-3010",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-303-515-7627",
    "303-515-7627",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-719-208-7763",
    "719-208-7763",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-303-515-7619",
    "303-515-7619",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    ["Shared by JeffCo Probation and a Canon City / 11th JD research note."],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-303-515-7632",
    "303-515-7632",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine(
    "line-970-295-4766",
    "970-295-4766",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
  buildPhoneLine("line-970-279-8472", "970-279-8472", "Recovery Resources", [], [], "unknown"),
  buildPhoneLine("line-719-543-1000", "719-543-1000", "Intervention Inc", [], [], "unknown", [
    "Managed via Intervention Inc.",
  ]),
  buildPhoneLine(
    "line-970-336-7227",
    "970-336-7227",
    "Weld County Pretrial Services",
    [],
    [],
    "unknown",
    ["Pretrial Services line."]
  ),
  buildPhoneLine("line-970-945-8323", "970-945-8323", "Alpine Springs Testing", [], [], "unknown", [
    "Alpine Springs testing line.",
  ]),
  buildPhoneLine("line-970-256-7772", "970-256-7772", "Procom / MCC Testing", [], [], "unknown", [
    "Procom / MCC testing line.",
  ]),
  buildPhoneLine("line-719-539-2855", "719-539-2855", "Unknown", [], [], "unknown", [
    "The source notes an extension 248, but does not document the IVR prompts.",
  ]),
  buildPhoneLine("line-303-772-4494", "303-772-4494", "Intervention Longmont", [], [], "unknown", [
    "Specific to the Longmont Intervention line.",
  ]),
  buildPhoneLine(
    "line-720-710-4359",
    "720-710-4359",
    "Sentry UA",
    ["participantId"],
    ["languageSelection"],
    "daily-test-status",
    [],
    ["ua-testing-co-phones", "sentry-ua-reference"],
    SENTRY_PROMPT_EXAMPLE
  ),
];

export const coProbationProgramCallOptions: CoProbationProgramCallOption[] = [
  {
    id: "denver-county-court-probation",
    label: "Denver County Court Probation",
    phoneLineId: "line-303-515-7638",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones", "sentry-ua-reference"],
  },
  {
    id: "adams-boulder-17th-20th",
    label: "Adams / Boulder (17th/20th)",
    phoneLineId: "line-303-785-6174",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "arapahoe-county-18th-jd",
    label: "Arapahoe County / 18th JD",
    phoneLineId: "line-303-268-3010",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "douglas-county-pre-trial",
    label: "Douglas County Pre-Trial",
    phoneLineId: "line-303-515-7627",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "el-paso-teller-4th-jd",
    label: "El Paso / Teller (4th JD)",
    phoneLineId: "line-719-208-7763",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "jeffco-probation-1st-jd",
    label: "JeffCo Probation (1st JD)",
    phoneLineId: "line-303-515-7619",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "jeffco-diversion",
    label: "JeffCo Diversion",
    phoneLineId: "line-303-515-7632",
    possibleUserInputs: ["assignedColor"],
    notes: ["Jefferson County diversion programs are called out as likely color-code users."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "larimer-county-8th-jd",
    label: "Larimer County (8th JD)",
    phoneLineId: "line-970-295-4766",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "recovery-resources",
    label: "Recovery Resources",
    phoneLineId: "line-970-279-8472",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "pueblo-10th-jd",
    label: "Pueblo / 10th JD",
    phoneLineId: "line-719-543-1000",
    possibleUserInputs: [],
    notes: ["Managed via Intervention Inc."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "weld-county-19th-jd",
    label: "Weld County / 19th JD",
    phoneLineId: "line-970-336-7227",
    possibleUserInputs: [],
    notes: ["Pretrial Services line."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "canon-city-11th-jd",
    label: "Canon City / 11th JD",
    phoneLineId: "line-303-515-7619",
    possibleUserInputs: [],
    notes: ["The source notes that this line is shared with JeffCo / Intervention."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "glenwood-springs-9th-jd",
    label: "Glenwood Springs / 9th JD",
    phoneLineId: "line-970-945-8323",
    possibleUserInputs: [],
    notes: ["Alpine Springs testing line."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "grand-junction-21st-jd",
    label: "Grand Junction / 21st JD",
    phoneLineId: "line-970-256-7772",
    possibleUserInputs: [],
    notes: ["Procom / MCC testing line."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "salida-11th-jd",
    label: "Salida / 11th JD",
    phoneLineId: "line-719-539-2855",
    possibleUserInputs: [],
    notes: ["Extension 248 is documented, but the IVR flow is not."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "longmont",
    label: "Longmont",
    phoneLineId: "line-303-772-4494",
    possibleUserInputs: [],
    notes: ["Specific to Longmont Intervention."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "18th-jd-juvenile-diversion",
    label: "18th JD Juvenile Diversion",
    phoneLineId: "line-303-268-3010",
    possibleUserInputs: ["assignedColor"],
    notes: ["The source specifically flags juvenile diversion as a likely color-code workflow."],
    sourceDocumentIds: ["ua-testing-co-phones", "sentry-ua-reference"],
  },
  {
    id: "18th-jd-diversion-counseling",
    label: "18th JD Diversion Counseling",
    phoneLineId: "line-720-710-4359",
    possibleUserInputs: ["assignedColor"],
    notes: ["The source specifically flags diversion counseling as a likely color-code workflow."],
    sourceDocumentIds: ["ua-testing-co-phones", "sentry-ua-reference"],
  },
  {
    id: "jefferson-county-diversion",
    label: "Jefferson County Diversion",
    phoneLineId: "line-303-515-7632",
    possibleUserInputs: ["assignedColor"],
    notes: ["Jefferson County diversion programs are called out as likely color-code users."],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "recovery-resources-various",
    label: "Recovery Resources (Various)",
    phoneLineId: "line-970-279-8472",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones"],
  },
  {
    id: "arapahoe-judicial-services",
    label: "Arapahoe Judicial Services",
    phoneLineId: "line-303-268-3010",
    possibleUserInputs: [],
    notes: [],
    sourceDocumentIds: ["ua-testing-co-phones", "sentry-ua-reference"],
  },
];

export const coProbationResearchGaps: CoProbationResearchGap[] = [
  {
    id: "federal-probation-phone-number-missing",
    summary:
      "The Colorado federal probation color-code workflow is mentioned in the notes, but no phone number is documented yet.",
  },
  {
    id: "line-specific-name-or-dob-verification",
    summary:
      "The current research does not document any Colorado lines that explicitly verify name or date of birth before giving the test result.",
  },
];

const phoneLinesById = new Map(coProbationPhoneLines.map((line) => [line.id, line]));
const programCallOptionsById = new Map(
  coProbationProgramCallOptions.map((option) => [option.id, option])
);

export function getCoProbationProgramCallProfile(
  programId: string
): CoProbationProgramCallProfile | undefined {
  const option = programCallOptionsById.get(programId);
  if (!option) {
    return undefined;
  }

  const line = phoneLinesById.get(option.phoneLineId);
  if (!line) {
    return undefined;
  }

  const documentedUserInputs = [...line.documentedUserInputs];
  const possibleUserInputs = uniqueInputs([
    ...line.possibleUserInputs,
    ...option.possibleUserInputs,
  ]).filter((input) => !documentedUserInputs.includes(input));

  return {
    id: option.id,
    label: option.label,
    phoneLineId: line.id,
    phoneNumber: line.phoneNumber,
    serviceProvider: line.serviceProvider,
    resultMode: line.resultMode,
    documentedUserInputs,
    possibleUserInputs,
    notes: [...line.notes, ...option.notes],
  };
}
